import { ApiException } from '../../../api/api';

export interface FlowValidationIssue {
	code: string;
	path: string;
	message: string;
}

export interface FlowSaveError {
	title: string;
	detail: string;
	traceId?: string;
	validationIssues: FlowValidationIssue[];
}

interface ProblemDetailsResponse {
	title?: string;
	detail?: string;
	traceId?: string;
	TraceId?: string;
}

const VALIDATION_HEADER = 'Flow definition validation failed:';
const VALIDATION_ISSUE_PATTERN = /^\[([^\]]+)]\s+([^:]+):\s*(.+)$/;

export function parseFlowSaveError(error: unknown): FlowSaveError {
	const fallback: FlowSaveError = {
		title: 'Failed to save flow',
		detail: error instanceof Error && error.message ? error.message : 'An unexpected error occurred while saving the flow.',
		validationIssues: [],
	};

	if (typeof error !== 'object' || error === null || !ApiException.isApiException(error)) {
		return fallback;
	}

	try {
		const problem = JSON.parse(error.response) as ProblemDetailsResponse;
		const detail = problem.detail?.trim() || fallback.detail;
		const validationIssues = parseFlowValidationIssues(detail);

		return {
			title: validationIssues.length > 0 ? 'Flow validation failed' : problem.title?.trim() || fallback.title,
			detail,
			traceId: problem.traceId ?? problem.TraceId,
			validationIssues,
		};
	} catch {
		return fallback;
	}
}

export function parseFlowValidationIssues(detail: string): FlowValidationIssue[] {
	const lines = detail
		.replace(VALIDATION_HEADER, '')
		.split(/\r?\n/)
		.map((line) => line.trim())
		.filter(Boolean);

	return lines.flatMap((line) => {
		const match = VALIDATION_ISSUE_PATTERN.exec(line);
		if (!match) {
			return [];
		}

		return [{ code: match[1], path: match[2].trim(), message: match[3].trim() }];
	});
}
